
import './App.css';
import { Suspense, lazy, useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from "react-redux"
import { Routes, Route, useLocation } from 'react-router-dom'
import { Spin } from 'antd'
import Home from './views/home'
import Header from './common/header/index.jsx'
import Bottom from './common/bottom'
import Login from '@/components/Login'
import { getUserinfo, getMyFollows, getMyFans, userHeartbeat, getNotificationList } from '@/api'
import { setUser, setMyFollows, setMyFans, setNotifications } from '@/redux/modules/user/action'
import mqtt from 'mqtt'

const AboutUs = lazy(() => import('@/views/aboutUs'))
const Detail = lazy(() => import('@/views/detail'))
const Notification = lazy(() => import('@/views/notification'))
const UploadModel = lazy(() => import('@/views/UploadPage/Model/index.jsx'))
const UploadMaterial = lazy(() => import('@/views/UploadPage/Material/index.jsx'))
const ModelListByTag = lazy(() => import('@/views/list/tags/index.jsx'))
const ModelListByCategory = lazy(() => import('@/views/list/category/index.jsx'))
const ModelListBySubCategory = lazy(() => import('@/views/list/sub-category/index.jsx'))
const UserModel = lazy(() => import('@/views/user/model'))
const UserCollect = lazy(() => import('@/views/user/collect'))
const UserHistory = lazy(() => import('@/views/user/history'))
const UserOverview = lazy(() => import('@/views/user/overview'))
const UserCreator = lazy(() => import('@/views/user/creator'))
const UserSettingProfile = lazy(() => import('@/views/user/setting/profile'))
const UserSettingAccount = lazy(() => import('@/views/user/setting/account'))
const Search = lazy(() => import('@/views/search'))
const Test = lazy(() => import('@/views/test'))

let heartbeatInterval = null

const App = () => {

  const location = useLocation()
  const showLogin = useSelector((state) => state.user.showLogin)
  const notifications = useSelector((state) => state.user.notifications)
  const token = useSelector((state) => state.user.token)
  const user = useSelector((state) => state.user.userInfo)
  const [client, setClient] = useState(null)
  const dispatch = useDispatch()

  const fetchUserInfos = useCallback(async () => {
    try {
      const info = await getUserinfo()
      if (info.code === 0) {
        dispatch(setUser(info.data))
      }
    } catch (e) {
      console.log(e)
    }
    try {
      const followsInfo = await getMyFollows()
      if (followsInfo.code === 0) {
        dispatch(setMyFollows(followsInfo.data || []))
      }
    } catch (e) {
      console.log(e)
    }
    try {
      const fansInfo = await getMyFans()
      if (fansInfo.code === 0) {
        dispatch(setMyFans(fansInfo.data || []))
      }
    } catch (e) {
      console.log(e)
    }
  }, [dispatch])

  const handleHeartbeat = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
    }
    heartbeatInterval = setInterval(() => {
      // 只有在有 token 的情况下才发送心跳
      if (token) {
        userHeartbeat()
      }
    }, 30000)
  }

  const fetchNotificationList = async () => {
    // 只有在用户登录并且有 token 的情况下才请求通知列表
    if (!token) {
      return
    }

    try {
      const res = await getNotificationList()
      if (res.code === 0) {
        const msgMap = new Map()
        res.data.forEach(item => {
          msgMap.set(item.id, item)
        })
        dispatch(setNotifications(Array.from(msgMap.values())))
      }
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    if (!token) {
      clearInterval(heartbeatInterval)
      heartbeatInterval = null
    }
  }, [token])

  useEffect(() => {
    if (user && user.id) {
      fetchNotificationList()
      handleHeartbeat()
    }
  }, [user])

  useEffect(() => {
    // 从配置文件获取MQTT连接信息
    const { mqttConfig } = require('@/config')
    const mqttClient = mqtt.connect(mqttConfig.url, {
      username: mqttConfig.username,
      password: mqttConfig.password,
      reconnectPeriod: mqttConfig.reconnectPeriod,
      connectTimeout: mqttConfig.connectTimeout,
    })
    mqttClient.on('error', (err) => {
      console.error('MQTT连接错误: ', err);
    })
    mqttClient.on('connect', () => {
      console.log('MQTT连接成功');
    })
    setClient(mqttClient)

    // 清理连接
    return () => {
      if (mqttClient) {
        mqttClient.end()
      }
    }
  }, [])

  useEffect(() => {
    if (client && user && user.id) {
      client.on('connect', () => {
        client.subscribe(`msg/unread/user_${user.id}`)
        client.subscribe(`msg/read/user_${user.id}`)
      })
      client.on('message', (topic, message) => {
        if (topic.includes('read/user_')) {
          const msgMap = new Map()
          notifications.forEach(item => {
            msgMap.set(item.id, item)
          })
          const lastMsg = JSON.parse(message.toString())
          msgMap.set(lastMsg.id, lastMsg)
          dispatch(setNotifications(Array.from(msgMap.values())))
          fetchNotificationList()
        }
      })
    }
  }, [client, user])

  useEffect(() => {
    // 在mock模式下，直接设置用户数据，跳过所有登录检查
    if (process.env.REACT_APP_USE_MOCK === 'true') {
      const mockUser = {
        id: 1,
        nickname: '测试用户',
        avatar: '/images/defaulticon.png',
        email: 'test@example.com'
      }
      dispatch(setUser(mockUser))
      // 同时设置登录状态
      dispatch({ type: 'LOGIN', payload: { username: mockUser.nickname } })
      // 强制关闭登录界面
      dispatch({ type: 'SET_SHOW_LOGIN', status: false })
    } else {
      // 非mock模式下才检查token
      if (!token) {
        const mockUser = {
          id: 1,
          nickname: '测试用户',
          avatar: '/images/defaulticon.png',
          email: 'test@example.com'
        }
        dispatch(setUser(mockUser))
      } else {
        token && fetchUserInfos()
      }
    }
  }, [token, fetchUserInfos, dispatch])

  return (
    <>
      <Header />
      <div className='router-view' style={{ background: location.pathname === '/' ? '#fbfbfd' : '#ffffff' }}>
        <div className='router-view-wrap'>
          <Suspense fallback={
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '60vh'
            }}>
              <Spin size="large" tip="加载中...">
                <div style={{ padding: 50 }} />
              </Spin>
            </div>
          }>
            <Routes>
              <Route path='/' element={<Home />}></Route>
              <Route path='/category' exact element={<ModelListByCategory />}></Route>
              <Route path='/category/:category' exact element={<ModelListBySubCategory />}></Route>
              <Route path='/aboutUs' element={<AboutUs />}></Route>
              <Route path='/detail/:id' element={<Detail />}></Route>
              <Route path='/notification' element={<Notification />}></Route>
              <Route path='/UploadPage/Model' element={<UploadModel />}></Route>
              <Route path='/UploadPage/Material' element={<UploadMaterial />}></Route>
              <Route path='/EditModel/:id' element={<UploadModel />}></Route>
              <Route path='/tags/:tag' element={<ModelListByTag />}></Route>
              <Route path='/user/overview/:uid' element={<UserOverview />}></Route>
              <Route path='/user/creator/:uid' element={<UserCreator />}></Route>
              <Route path='/user/model' element={<UserModel />}></Route>
              <Route path='/user/collect' element={<UserCollect />}></Route>
              <Route path='/user/history' element={<UserHistory />}></Route>
              <Route path='/user/setting/profile' element={<UserSettingProfile />}></Route>
              <Route path='/user/setting/account' element={<UserSettingAccount />}></Route>
              <Route path='/search' element={<Search />}></Route>
            </Routes>
          </Suspense>
        </div>
      </div>
      <Bottom></Bottom>
      <Login open={ showLogin }></Login>
    </>
  )
}

export default App
